import type { AssetsPortalSchemaWithIdNullable } from '@iconik-io/iconik-api/src/services/assets';
import { findBestKeyframe } from '@iconik-io/shared-lib/src/utils/find_best_keyframe';
import { stabilizeMediaUrl } from '@iconik-io/shared-lib/src/utils/stabilize_media_url';

type PortalKeyframe = NonNullable<
  AssetsPortalSchemaWithIdNullable['keyframes']
>[number];

// The portal exposes no cover pointer; BE-4249 projects the chosen cover as
// custom keyframes without an asset_id, while the item keyframes BE-4797 falls
// back to carry one.
function isOwnCoverKeyframe(keyframe: PortalKeyframe): boolean {
  return Boolean(keyframe.is_custom_keyframe) && !keyframe.asset_id;
}

export function getPortalCoverUrl(
  portal: AssetsPortalSchemaWithIdNullable | undefined,
): string | undefined {
  return (
    stabilizeMediaUrl(
      portal?.keyframes?.find(
        (keyframe) =>
          isOwnCoverKeyframe(keyframe) && keyframe.type === 'POSTER',
      )?.url,
    ) ?? undefined
  );
}

/**
 * The small cover variant, for cards and previews rather than the full-bleed hero.
 * Covers saved before FE-7431 have no small variant, so fall back to the poster.
 */
export function getPortalCoverThumbnailUrl(
  portal: AssetsPortalSchemaWithIdNullable | undefined,
): string | undefined {
  const thumbnailUrl = portal?.keyframes?.find(
    (keyframe) => isOwnCoverKeyframe(keyframe) && keyframe.type === 'KEYFRAME',
  )?.url;
  return stabilizeMediaUrl(thumbnailUrl) ?? getPortalCoverUrl(portal);
}

export function getPortalFallbackCoverUrl(
  portal: AssetsPortalSchemaWithIdNullable | undefined,
): string | undefined {
  const fallbackKeyframes = portal?.keyframes?.filter(
    (keyframe) => !isOwnCoverKeyframe(keyframe),
  );
  return (
    stabilizeMediaUrl(findBestKeyframe(fallbackKeyframes)?.url) ?? undefined
  );
}
